import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchGlDepartments, fetchLaborDepartments, isAbortError, listUsers, loadDirectory } from './usersApi';
import { filtersToQuery } from './userModel';

/**
 * Organizations the admin manages (their own orgAccess codes) with names,
 * numeric ids and PBI workspaces, plus every location they can see.
 */
export function useDirectory(viewer) {
  const [raw, setRaw] = useState({ loading: true, orgs: [], configs: [], locations: [] });

  useEffect(() => {
    let alive = true;
    loadDirectory().then((d) => { if (alive) setRaw({ loading: false, ...d }); });
    return () => { alive = false; };
  }, []);

  return useMemo(() => {
    const sqlByCode = new Map(raw.orgs.filter((o) => o?.code).map((o) => [String(o.code), o]));
    const cfgByCode = new Map(raw.configs.filter((c) => c?.orgId).map((c) => [String(c.orgId), c]));
    const orgNameByCode = {};
    sqlByCode.forEach((o, code) => { orgNameByCode[code] = o.name || code; });

    const viewerCodes = Object.keys(viewer?.orgAccess || {});
    const orgs = viewerCodes.map((code) => {
      const sql = sqlByCode.get(code);
      const cfg = cfgByCode.get(code);
      const id = sql?.id ?? cfg?.dbOrgId ?? null;
      return {
        code,
        id: id === null || id === undefined ? null : Number(id),
        name: sql?.name || cfg?.name || code,
        workspaceId: cfg?.workspaceId || null,
      };
    }).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    orgs.forEach((o) => { orgNameByCode[o.code] = o.name; });

    const byCode = new Map(orgs.map((o) => [o.code, o]));
    const byId = new Map(orgs.filter((o) => o.id !== null).map((o) => [String(o.id), o]));

    const locationsByOrgId = new Map();
    const locationById = new Map();
    raw.locations.forEach((loc) => {
      if (!loc || loc.isDeleted === 1 || loc.isDeleted === true) return;
      locationById.set(String(loc.id), loc);
      const key = String(loc.orgId);
      if (!locationsByOrgId.has(key)) locationsByOrgId.set(key, []);
      locationsByOrgId.get(key).push(loc);
    });
    locationsByOrgId.forEach((list) => list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));

    return {
      loading: raw.loading,
      orgs,
      byCode,
      byId,
      manageable: new Set(viewerCodes),
      orgNameByCode,
      locations: raw.locations,
      locationById,
      locationsForOrg: (code) => {
        const org = byCode.get(code);
        return org && org.id !== null ? locationsByOrgId.get(String(org.id)) || [] : [];
      },
    };
  }, [raw, viewer]);
}

// Department lists rarely change; one fetch per org per page visit.
const deptCache = new Map();
function cachedDepartments(kind, dbOrgId) {
  const key = `${kind}:${dbOrgId}`;
  if (!deptCache.has(key)) {
    deptCache.set(key, (kind === 'gl' ? fetchGlDepartments : fetchLaborDepartments)(dbOrgId));
  }
  return deptCache.get(key);
}

/** { labor, gl } department lists for one org (null while loading). */
export function useOrgDepartments(dbOrgId, { labor = true } = {}) {
  const [state, setState] = useState({ labor: null, gl: null });
  useEffect(() => {
    if (dbOrgId === null || dbOrgId === undefined) { setState({ labor: [], gl: [] }); return undefined; }
    let alive = true;
    const jobs = [cachedDepartments('gl', dbOrgId), labor ? cachedDepartments('labor', dbOrgId) : Promise.resolve([])];
    Promise.all(jobs).then(([gl, lab]) => { if (alive) setState({ gl, labor: lab }); });
    return () => { alive = false; };
  }, [dbOrgId, labor]);
  return state;
}

/**
 * Paged admin list. Every change aborts the in-flight request, so a slow
 * earlier search can never overwrite a newer one. Rows from the previous
 * page stay visible while the next loads (`loading` + `rows` both set).
 */
export function useUsersList(filters) {
  const [state, setState] = useState({ rows: [], total: 0, counts: null, loading: true, error: null, loaded: false });
  const [nonce, setNonce] = useState(0);
  const query = useMemo(() => filtersToQuery(filters), [filters]);
  const key = JSON.stringify(query);
  const ctrlRef = useRef(null);

  useEffect(() => {
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    setState((s) => ({ ...s, loading: true, error: null }));
    listUsers(JSON.parse(key), { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setState({ rows: res.users, total: res.total, counts: res.counts, loading: false, error: null, loaded: true });
      })
      .catch((err) => {
        if (ctrl.signal.aborted || isAbortError(err)) return;
        setState((s) => ({ ...s, loading: false, error: err }));
      });
    return () => ctrl.abort();
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const patchRow = useCallback((id, next) => {
    setState((s) => ({ ...s, rows: s.rows.map((r) => (r.id === id ? (typeof next === 'function' ? next(r) : { ...r, ...next }) : r)) }));
  }, []);
  const removeRow = useCallback((id) => {
    setState((s) => ({ ...s, rows: s.rows.filter((r) => r.id !== id), total: Math.max(0, s.total - 1) }));
  }, []);

  return { ...state, reload, patchRow, removeRow };
}

/** Debounced value (search box → URL). */
export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
